import { RefObject } from "react";
import { Topic } from "./types";

interface SidebarProps {
    sidebarWidth: number;
    isResizing: boolean;
    setIsResizing: (isResizing: boolean) => void;
    course: any;
    topics: Topic[];
    activeTopicId: string | null;
    setActiveTopicId: (id: string | null) => void;
    newTopicTitle: string;
    setNewTopicTitle: (title: string) => void;
    createTopic: () => void;
    navigate: (path: string) => void;
    isDesktop: boolean;
    onCloseMobile?: () => void;
}

export default function Sidebar({
                                    sidebarWidth,
                                    isResizing,
                                    setIsResizing,
                                    course,
                                    topics,
                                    activeTopicId,
                                    setActiveTopicId,
                                    newTopicTitle,
                                    setNewTopicTitle,
                                    createTopic,
                                    navigate,
                                    isDesktop,
                                    onCloseMobile
                                }: SidebarProps) {
    return (
        <aside
            className="h-full w-full bg-white/95 backdrop-blur-xl border-r border-gray-200 p-4 md:p-6 flex flex-col transition-all duration-200 overflow-hidden"
        >
            {/* Close Button für Mobile */}
            <button
                onClick={onCloseMobile}
                className="lg:hidden absolute top-3 right-3 text-gray-400 hover:text-gray-700 p-1"
            >
                ✕
            </button>

            {/* Header */}
            <div className="mb-4 md:mb-6 flex-shrink-0">
                <h2 className="text-lg md:text-xl font-bold mb-2 md:mb-3 text-gray-800">
                    📚 Themen
                </h2>
                <div className="flex flex-col gap-1">
                    <span className="text-gray-700 font-medium text-xs md:text-sm truncate">
                        {course.title}
                    </span>
                    {course.grade && (
                        <span className="text-green-600 text-xs bg-green-50 px-2 py-1 rounded w-fit mt-1">
                            Klasse {course.grade}
                        </span>
                    )}
                </div>
            </div>

            {/* Create Topic */}
            <div className="mb-4 flex-shrink-0">
                <div className="flex flex-col gap-2">
                    <input
                        placeholder="Neues Thema hinzufügen..."
                        value={newTopicTitle}
                        onChange={(e) => setNewTopicTitle(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && createTopic()}
                        className="w-full px-3 py-2 rounded-lg bg-gray-50 border border-gray-300 text-gray-800 text-xs md:text-sm transition-all focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 placeholder:text-gray-400"
                    />
                    <button
                        onClick={createTopic}
                        disabled={!newTopicTitle.trim()}
                        className="w-full py-2 rounded-lg bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold cursor-pointer flex items-center justify-center gap-1.5 transition-all hover:shadow-lg hover:shadow-green-500/20 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:transform-none text-sm"
                    >
                        <span>+</span>
                        <span>Hinzufügen</span>
                    </button>
                </div>
            </div>

            {/* Topics List */}
            <div className="flex-1 overflow-hidden min-h-0">
                <div className="h-full overflow-y-auto pr-1 md:pr-2 scrollbar-thin scrollbar-thumb-green-500/30 scrollbar-track-transparent">
                    {topics.length === 0 ? (
                        <div className="text-center py-6 px-4 text-gray-500">
                            <div className="text-2xl md:text-3xl mb-2 opacity-50">📂</div>
                            <p className="font-medium text-sm">Noch keine Themen</p>
                            <small className="text-xs">Erstelle dein erstes Thema</small>
                        </div>
                    ) : (
                        <ul className="space-y-1">
                            {topics.map((topic) => (
                                <li
                                    key={topic.id}
                                    className={`relative flex items-center gap-2 p-2 md:p-3 rounded-lg cursor-pointer transition-all ${
                                        topic.id === activeTopicId
                                            ? 'bg-green-50 border border-green-200 shadow-sm'
                                            : 'hover:bg-gray-100 border border-transparent'
                                    } ${topic.id.startsWith('temp-') ? 'opacity-70 animate-pulse' : ''}`}
                                    onClick={() => {
                                        setActiveTopicId(topic.id);
                                        if (onCloseMobile) onCloseMobile();
                                    }}
                                >
                                    <div className="text-base md:text-lg">
                                        {topic.files && topic.files.length > 0 ? '📄' : '📝'}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <span className="block font-medium text-xs md:text-sm text-gray-800 truncate">
                                            {topic.title}
                                        </span>
                                        {topic.files && topic.files.length > 0 && (
                                            <span className="block text-xs text-gray-500 mt-0.5">
                                                {topic.files.length} Datei{topic.files.length !== 1 ? 'en' : ''}
                                            </span>
                                        )}
                                    </div>
                                    {topic.id !== activeTopicId && (
                                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-green-500/10 to-transparent opacity-0 hover:opacity-100 transition-opacity" />
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            {/* Footer */}
            <div className="mt-auto pt-3 md:pt-4 border-t border-gray-200 flex-shrink-0">
                <button
                    onClick={() => {
                        navigate("/courses");
                        if (onCloseMobile) onCloseMobile();
                    }}
                    className="w-full py-2 px-3 bg-transparent border border-gray-300 rounded-lg text-gray-600 cursor-pointer transition-all hover:bg-gray-100 hover:text-gray-800 hover:border-green-500 text-xs md:text-sm"
                >
                    ← Alle Kurse
                </button>
            </div>

            {/* Resize Handle nur auf Desktop */}
            {isDesktop && (
                <div
                    className="absolute right-0 top-0 bottom-0 w-2.5 cursor-col-resize flex items-center justify-center z-30 transition-colors hover:bg-green-500/10"
                    onMouseDown={() => setIsResizing(true)}
                >
                    <div className="flex flex-col gap-0.5 opacity-50 hover:opacity-100 transition-opacity">
                        <div className="w-0.5 h-0.5 bg-green-500 rounded-full"></div>
                        <div className="w-0.5 h-0.5 bg-green-500 rounded-full"></div>
                        <div className="w-0.5 h-0.5 bg-green-500 rounded-full"></div>
                    </div>
                </div>
            )}
        </aside>
    );
}